import posthogProject from '../../../../config/posthog-channelplex.json';
import { createProductMeasurementController, PRODUCT_EVENTS, sanitizeProductEventProperties, productEventMetadata } from '../../../../shared/productAnalytics';

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY?.trim();
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST?.trim() || posthogProject.apiHost;

export function resolveWebProductEnvironment(configured: unknown, isDevelopment: boolean): string {
  if (isDevelopment) return 'development';
  return typeof configured === 'string' && ['production', 'preview', 'qa', 'staging', 'local', 'test'].includes(configured)
    ? configured : 'unknown';
}
const PRODUCT_ENVIRONMENT = resolveWebProductEnvironment(import.meta.env.VITE_CHANNELPLEX_ANALYTICS_ENV, import.meta.env.DEV);

export const ANALYTICS_APP = 'channelplex';
export const ANALYTICS_SURFACE = 'web';

const BASE_PROPERTIES = {
  app: ANALYTICS_APP,
  surface: ANALYTICS_SURFACE,
};

const MAX_QUEUED_ACTIONS = 100;
const UNKNOWN_ANALYTICS_ROUTE = '/unknown';

type AnalyticsProperties = Record<string, unknown>;

interface AnalyticsClient {
  init: (key: string, options: Record<string, unknown>) => unknown;
  register: (properties: AnalyticsProperties) => void;
  capture: (event: string, properties?: AnalyticsProperties) => void;
  identify: (distinctId: string, properties?: AnalyticsProperties) => void;
  reset: () => void;
  get_distinct_id?: () => string;
  get_property?: (key: string) => unknown;
}

type QueuedAnalyticsAction =
  | { type: 'capture'; event: string; properties: AnalyticsProperties; contextRevision?: number }
  | { type: 'identify'; distinctId: string; properties: AnalyticsProperties }
  | { type: 'register'; properties: AnalyticsProperties };

interface LazyAnalyticsBridgeOptions {
  key?: string;
  host: string;
  loadClient: () => Promise<AnalyticsClient>;
  maxQueueSize?: number;
}

export interface AnalyticsIdentity {
  userId?: number | string | null;
  userCode?: number | string | null;
}

export interface AnalyticsCaptureResult {
  event: string;
  uuid?: string;
  timestamp?: Date;
  properties: AnalyticsProperties;
  $set?: AnalyticsProperties;
  $set_once?: AnalyticsProperties;
  $unset?: string[];
  [key: string]: unknown;
}

const ROUTE_TEMPLATES: Array<{ pattern: RegExp; template: string }> = [
  // A dashboard someone built: named as one route, never by which dashboard it is.
  { pattern: /^\/dashboard\/custom\/[^/]+$/i, template: '/dashboard/custom/:id' },
  { pattern: /^\/reports\/task-response\/[^/]+$/i, template: '/reports/task-response/:taskId' },
  { pattern: /^\/lms\/content\/[^/]+\/preview$/i, template: '/lms/content/:id/preview' },
  { pattern: /^\/lms\/content\/[^/]+\/assignments$/i, template: '/lms/content/:id/assignments' },
  { pattern: /^\/lms\/content\/[^/]+\/learners$/i, template: '/lms/content/:id/learners' },
  { pattern: /^\/lms\/content\/(?!new$)[^/]+$/i, template: '/lms/content/:id' },
  { pattern: /^\/lms\/journeys\/[^/]+\/assignments$/i, template: '/lms/journeys/:id/assignments' },
  { pattern: /^\/lms\/journeys\/(?!new$)[^/]+$/i, template: '/lms/journeys/:id' },
  { pattern: /^\/lms\/sessions\/[^/]+\/learners$/i, template: '/lms/sessions/:id/learners' },
  { pattern: /^\/lms\/sessions\/[^/]+\/assignments$/i, template: '/lms/sessions/:id/assignments' },
  { pattern: /^\/lms\/sessions\/(?!new$)[^/]+$/i, template: '/lms/sessions/:id' },
];

// Keep this in lockstep with the literal <Route path> values in App.tsx. The
// authenticated app has a wildcard shell, so passing unknown text through here
// would let arbitrary deep-link content become an analytics property.
const STATIC_ANALYTICS_PATHS = new Set([
  '/',
  '/activities/daily-attendance-logs',
  '/activities/random-attendance-logs',
  '/activities/task-logs',
  '/activities/task-management',
  '/approvals',
  '/approvals/probation-confirmation',
  '/dashboard',
  // One address per built-in dashboard (navigation/dashboards DASH_TAB_SLUG). The route
  // is /dashboard/:slug, which accepts any text, so only the fifteen real slugs are named;
  // anything else typed after /dashboard/ stays '/unknown'. The test fails if a
  // dashboard is added there and not here.
  '/dashboard/appeals',
  '/dashboard/approvals',
  '/dashboard/attendance',
  '/dashboard/attrition',
  '/dashboard/data-hygiene',
  '/dashboard/deployment',
  '/dashboard/ews',
  '/dashboard/hiring',
  '/dashboard/mood',
  '/dashboard/onboarding',
  '/dashboard/red-flag',
  '/dashboard/score-movement',
  '/dashboard/training',
  '/dashboard/uniform-assets',
  '/dashboard/yellow-flag',
  '/employee/master',
  // /governance, /grow, /master-management, /reports and /settings are the section
  // front pages (navigation/catalog SECTIONS).
  '/governance',
  '/governance/connect-content-policy',
  '/governance/social-moderation',
  '/governance/start-exit',
  '/grow',
  '/grow/leaderboard',
  '/grow/offline-trainings',
  '/grow/shadow-training',
  '/leave-policy',
  '/lms/content',
  '/lms/content/new',
  '/lms/journeys',
  '/lms/journeys/new',
  '/lms/sessions',
  '/lms/sessions/new',
  '/login',
  '/master-management',
  '/master-management/apple-scorecard',
  '/master-management/buddy-pool',
  '/master-management/employee-custom-fields',
  '/master-management/employees',
  '/master-management/grooming-prompt',
  '/master-management/kpi',
  '/master-management/market-regularization-reasons',
  '/master-management/onboarding-signoff',
  '/master-management/products',
  '/master-management/red-flag-rules',
  '/master-management/sales-targets',
  '/master-management/store-categories',
  '/master-management/store-visit-forms',
  '/master-management/stores',
  '/master-management/targets',
  '/master-management/tasks',
  '/master-management/trainings',
  '/master-management/yellow-flag-reasons',
  // The person's own Messages. The open conversation travels in the query (?c= or ?g=),
  // which is never kept, so the page is named and the conversation is not.
  '/messages',
  '/reports',
  '/reports/appeal-workflow',
  '/reports/apple-scorecard',
  '/reports/content-modules',
  '/reports/daily-attendance',
  '/reports/employee-quadrants',
  '/reports/employee-score',
  '/reports/grooming',
  '/reports/learning-journeys',
  '/reports/mobile-usage',
  '/reports/mood',
  '/reports/pip',
  '/reports/quiz-performance',
  '/reports/random-attendance',
  '/reports/running-stock',
  '/reports/scorm-progress',
  '/reports/store-visits',
  '/reports/store-visit-tasks',
  '/reports/task-response',
  '/reports/task-response-v2',
  '/reports/yellow-flag-appeals',
  '/reports/yellow-flags',
  // "Where do you want to look?", the second step of search. Who or what it is about
  // travels in the query (?kind=&id=), which is never kept, so the page is named and
  // the person or store is not. What was typed into search is in no address at all.
  '/search',
  '/settings',
  '/settings/access-management',
  '/settings/distribution-lists',
  '/settings/focus-debug',
  '/settings/mailers',
  '/settings/menus',
  '/settings/mobile-access',
  '/settings/projects',
  '/settings/push-test',
  '/settings/roles',
  '/settings/user-roles',
  '/stats',
  '/store-categories',
  '/stores',
]);

const QUERY_ATTRIBUTION_PROPERTY_SUFFIXES = [
  'utmsource',
  'utmmedium',
  'utmcampaign',
  'utmcontent',
  'utmterm',
  'gadsource',
  'mccid',
  'gclid',
  'gclsrc',
  'dclid',
  'gbraid',
  'wbraid',
  'fbclid',
  'msclkid',
  'twclid',
  'lifatid',
  'igshid',
  'ttclid',
  'rdtcid',
  'epik',
  'qclid',
  'sccid',
  'irclid',
  'kx',
] as const;

const SENSITIVE_PROPERTY_KEYS = new Set([
  'authorization',
  'accesstoken',
  'refreshtoken',
  'idtoken',
  'portaltoken',
  'csrftoken',
  'password',
  'secret',
  'otp',
  'email',
  'emailaddress',
  'phone',
  'phonenumber',
  'name',
  'username',
  'firstname',
  'lastname',
  'fullname',
  'employeecode',
  'employeeid',
  'employeename',
  'manager',
  'managercode',
  'managername',
  'latitude',
  'longitude',
  'coordinates',
  'recordid',
  'taskid',
  'contentid',
  'journeyid',
  'lmssessionid',
  'assignmentid',
  'responseid',
  'title',
  'documenttitle',
  'pagetitle',
  'query',
  'search',
  'q',
  // PostHog derives these campaign values from the URL query before before_send.
  // They must be removed separately even though the URL itself is normalized.
  ...QUERY_ATTRIBUTION_PROPERTY_SUFFIXES,
]);

function normalizePropertyKey(key: string): string {
  return key.replace(/[$_.-]/g, '').toLowerCase();
}

function isSensitivePropertyKey(key: string): boolean {
  const normalized = normalizePropertyKey(key);
  const isReferrerVariant = normalized.includes('referrer')
    || normalized.includes('referringdomain');
  const isInitialOrSessionNavigation = (
    normalized.startsWith('initial') || normalized.startsWith('sessionentry')
  ) && (
    normalized.includes('url')
    || normalized.includes('pathname')
    || normalized.includes('host')
  );
  const isQueryAttribution = normalized.includes('keyword')
    || QUERY_ATTRIBUTION_PROPERTY_SUFFIXES.some((suffix) => normalized.endsWith(suffix));

  return SENSITIVE_PROPERTY_KEYS.has(normalized)
    || normalized.endsWith('token')
    || isReferrerVariant
    || isInitialOrSessionNavigation
    || isQueryAttribution;
}

/**
 * Converts application URLs into a stable, query-free route name. Only exact
 * current static paths and explicit dynamic templates are retained. Everything
 * else collapses to one safe bucket because the app's wildcard shell can accept
 * arbitrary deep-link text.
 */
export function normalizeAnalyticsPath(input: string): string {
  let pathname = input.trim() || '/';

  try {
    if (/^[a-z][a-z\d+.-]*:\/\//i.test(pathname)) {
      pathname = new URL(pathname).pathname;
    }
  } catch {
    // Fall through to the conservative delimiter stripping below.
  }

  pathname = pathname.split(/[?#]/, 1)[0] || '/';
  if (!pathname.startsWith('/')) pathname = `/${pathname}`;
  pathname = pathname.replace(/\/{2,}/g, '/');
  if (pathname.length > 1) pathname = pathname.replace(/\/+$/, '');

  for (const route of ROUTE_TEMPLATES) {
    if (route.pattern.test(pathname)) return route.template;
  }

  const canonicalStaticPath = pathname.toLowerCase();
  return STATIC_ANALYTICS_PATHS.has(canonicalStaticPath)
    ? canonicalStaticPath
    : UNKNOWN_ANALYTICS_ROUTE;
}

function sanitizeUrlString(value: string): string {
  const baseOrigin = typeof window === 'undefined' ? 'https://redacted.invalid' : window.location.origin;

  try {
    const url = new URL(value, baseOrigin);
    const safePath = normalizeAnalyticsPath(url.pathname);
    return value.startsWith('/') ? safePath : `${url.origin}${safePath}`;
  } catch {
    return UNKNOWN_ANALYTICS_ROUTE;
  }
}

export function getValidPostHogProjectToken(rawToken: unknown): string | null {
  const projectToken = typeof rawToken === 'string' ? rawToken.trim() : '';
  return projectToken === posthogProject.publicCaptureToken ? projectToken : null;
}

function sanitizeAnalyticsValue(value: unknown, key: string, depth: number): unknown {
  if (value == null || typeof value === 'number' || typeof value === 'boolean') return value;
  if (depth > 4) return undefined;
  if (typeof value === 'string') {
    const normalizedKey = normalizePropertyKey(key);
    const isUrlProperty = normalizedKey.includes('url')
      || normalizedKey.includes('pathname')
      || normalizedKey.includes('referrer')
      || normalizedKey === 'route';
    const looksLikeUrl = value.startsWith('/') || /^[a-z][a-z\d+.-]*:\/\//i.test(value);
    if (value === 'redacted') return value;
    return isUrlProperty || looksLikeUrl ? sanitizeUrlString(value) : value;
  }
  if (Array.isArray(value)) {
    return value
      .map((item) => sanitizeAnalyticsValue(item, key, depth + 1))
      .filter((item) => item !== undefined);
  }
  if (typeof value === 'object') {
    return sanitizeAnalyticsProperties(value as AnalyticsProperties, depth + 1);
  }
  return undefined;
}

/** Defense-in-depth for SDK-added properties and any future safe custom events. */
export function sanitizeAnalyticsProperties(
  properties: AnalyticsProperties,
  depth = 0,
): AnalyticsProperties {
  const sanitized: AnalyticsProperties = {};
  for (const [key, value] of Object.entries(properties)) {
    if (isSensitivePropertyKey(key)) continue;
    const safeValue = sanitizeAnalyticsValue(value, key, depth);
    if (safeValue !== undefined) sanitized[key] = safeValue;
  }
  return sanitized;
}

const ALLOWED_ANALYTICS_EVENTS = new Set(['$pageview', '$identify', ...PRODUCT_EVENTS]);

/**
 * Final outbound gate. PostHog calls this after adding browser/page properties,
 * so URL, title and PII filtering also covers SDK-enriched data. All automatic
 * and internal event types are dropped.
 */
export function filterAnalyticsEvent(
  captureResult: AnalyticsCaptureResult | null,
  projectToken?: string | null,
): AnalyticsCaptureResult | null {
  if (!captureResult || !ALLOWED_ANALYTICS_EVENTS.has(captureResult.event)) return null;

  const isProductEvent = (PRODUCT_EVENTS as readonly string[]).includes(captureResult.event);
  const properties = isProductEvent
    ? sanitizeProductEventProperties(captureResult.event, captureResult.properties || {})
    : sanitizeAnalyticsProperties(captureResult.properties || {});
  if (!properties) return null;
  // posthog-js DROPS any event whose SDK-set `token` property is missing after
  // before_send, and the generic `*token` rule above strips it — so without this,
  // nothing is ever sent. It is the public phc_ project token already shipped in the
  // bundle; restore it only on an exact match with the configured token so no other
  // token-shaped value survives (the mobile sanitizer applies the same rule).
  const configuredToken = getValidPostHogProjectToken(projectToken);
  if (configuredToken && captureResult.properties?.token === configuredToken) {
    properties.token = configuredToken;
  }

  if (isProductEvent) return { event: captureResult.event, properties, ...productEventMetadata(captureResult) };

  const sanitized: AnalyticsCaptureResult = {
    ...captureResult,
    properties,
  };
  if (captureResult.$set) sanitized.$set = sanitizeAnalyticsProperties(captureResult.$set);
  if (captureResult.$set_once) {
    sanitized.$set_once = sanitizeAnalyticsProperties(captureResult.$set_once);
  }
  if (captureResult.$unset) {
    sanitized.$unset = captureResult.$unset.filter((key) => !isSensitivePropertyKey(key));
  }
  return sanitized;
}

function normalizeIdentityPart(value: number | string | null | undefined): string | null {
  if (typeof value === 'number') return Number.isFinite(value) && value > 0 ? String(value) : null;
  if (typeof value !== 'string') return null;
  const normalized = value.trim();
  return normalized && /^[a-z\d_-]+$/i.test(normalized) ? normalized : null;
}

/** Numeric 1Office user id first; stable employee code only as a fallback. */
export function getAnalyticsDistinctId(user: AnalyticsIdentity | null | undefined): string | null {
  return normalizeIdentityPart(user?.userId) || normalizeIdentityPart(user?.userCode);
}

export function createLazyAnalyticsBridge(options: LazyAnalyticsBridgeOptions) {
  const projectToken = options.host.trim().replace(/\/+$/, '') === posthogProject.apiHost
    ? getValidPostHogProjectToken(options.key)
    : null;
  const maxQueueSize = Math.max(1, options.maxQueueSize ?? MAX_QUEUED_ACTIONS);
  const queue: QueuedAnalyticsAction[] = [];
  let client: AnalyticsClient | null = null;
  let initializationPromise: Promise<boolean> | null = null;
  let initializationAttempted = false;
  let initializationFailed = false;
  let desiredIdentity: string | null = null;
  let desiredIdentityProperties: AnalyticsProperties = {};
  let desiredIdentityPropertiesSignature = '';
  // PostHog persists identity in localStorage. An explicit unauthenticated
  // reset must still take effect if the SDK chunk has not loaded yet.
  let resetOnInitialize = false;
  let contextRevision = 0;
  let desiredProjectId: number | null = null;

  const enqueue = (action: QueuedAnalyticsAction) => {
    if (action.type === 'register') {
      const existingIndex = queue.findIndex((queued) => queued.type === 'register');
      if (existingIndex >= 0) queue.splice(existingIndex, 1);
    }
    if (queue.length >= maxQueueSize) {
      const oldestCaptureIndex = queue.findIndex((queued) => queued.type === 'capture');
      if (oldestCaptureIndex >= 0) queue.splice(oldestCaptureIndex, 1);
      else if (action.type === 'capture') return;
      else queue.shift();
    }
    queue.push(action);
  };

  const apply = (action: QueuedAnalyticsAction) => {
    if (!client) return;
    if (action.type === 'capture' && action.contextRevision !== undefined && action.contextRevision !== contextRevision) return;
    try {
      if (action.type === 'capture') client.capture(action.event, action.properties);
      else if (action.type === 'identify') {
        client.identify(action.distinctId, action.properties);
        if (Object.keys(action.properties).length > 0) client.register(action.properties);
      } else client.register(action.properties);
    } catch {
      // Analytics must never break the product or keep sending under uncertain
      // identity state. Disable this bridge instance after an SDK failure.
      client = null;
      initializationFailed = true;
      queue.length = 0;
    }
  };

  const measurements = createProductMeasurementController({
    context: () => projectToken && !initializationFailed && typeof window !== 'undefined' ? String(contextRevision) : null,
    capture: (event, properties) => {
      if (!projectToken || initializationFailed || typeof window === 'undefined') return false;
      const action: QueuedAnalyticsAction = { type: 'capture', event, properties: { ...properties, ...BASE_PROPERTIES, env: PRODUCT_ENVIRONMENT }, contextRevision };
      if (client) { apply(action); return client !== null; }
      enqueue(action);
      return true;
    },
  });

  const reset = () => {
    contextRevision += 1;
    desiredProjectId = null;
    measurements.clearAttribution();
    queue.length = 0;
    desiredIdentity = null;
    desiredIdentityProperties = {};
    desiredIdentityPropertiesSignature = '';
    if (client) {
      try {
        client.reset();
        client.register(BASE_PROPERTIES);
      } catch {
        client = null;
        resetOnInitialize = true;
      }
    } else {
      resetOnInitialize = true;
    }
  };

  const identify = (distinctId: string, properties: AnalyticsProperties = {}) => {
    if (!projectToken || typeof window === 'undefined' || !distinctId) return;
    const safeProperties = sanitizeAnalyticsProperties(properties);
    const propertiesSignature = JSON.stringify(safeProperties);
    if (desiredIdentity === distinctId && desiredIdentityPropertiesSignature === propertiesSignature) return;
    if (desiredIdentity && desiredIdentity !== distinctId) reset();
    else if (desiredIdentity !== distinctId) { contextRevision += 1; measurements.clearAttribution(); }

    if (desiredIdentity === distinctId && client) {
      // PostHog emits a `$set` for same-ID property changes. `$set` is not in
      // our outbound allowlist, so start a fresh anonymous identity and identify
      // back to the same stable user. This emits one sanitized `$identify` and
      // avoids permitting arbitrary internal `$set` events.
      try {
        client.reset();
        client.register(BASE_PROPERTIES);
      } catch {
        client = null;
        queue.length = 0;
        return;
      }
    } else if (desiredIdentity === distinctId) {
      // Replace an identity queued before the lazy SDK finishes loading.
      const queuedIdentityIndex = queue.findIndex((queued) => queued.type === 'identify');
      if (queuedIdentityIndex >= 0) queue.splice(queuedIdentityIndex, 1);
    }

    desiredIdentity = distinctId;
    desiredIdentityProperties = safeProperties;
    desiredIdentityPropertiesSignature = propertiesSignature;
    const action: QueuedAnalyticsAction = {
      type: 'identify',
      distinctId,
      properties: safeProperties,
    };
    if (client) apply(action);
    else enqueue(action);
  };

  const setProjectContext = (projectId: number | null | undefined) => {
    if (!projectToken || typeof window === 'undefined') return;
    const safeProjectId = typeof projectId === 'number' && Number.isSafeInteger(projectId) && projectId > 0
      ? projectId
      : null;
    if (safeProjectId !== desiredProjectId) { contextRevision += 1; desiredProjectId = safeProjectId; measurements.clearAttribution(); }
    const action: QueuedAnalyticsAction = {
      type: 'register',
      properties: { project_id: safeProjectId },
    };
    if (client) apply(action);
    else enqueue(action);
  };

  const capturePageview = (pathname: string, origin?: string) => {
    if (!projectToken || typeof window === 'undefined') return;
    const route = normalizeAnalyticsPath(pathname);
    let safeOrigin = '';
    try {
      safeOrigin = new URL(origin || window.location.origin).origin;
    } catch {
      // A path-only current URL is still safe and useful if origin is invalid.
    }
    const action: QueuedAnalyticsAction = {
      type: 'capture',
      event: '$pageview',
      properties: sanitizeAnalyticsProperties({
        ...BASE_PROPERTIES,
        route,
        $pathname: route,
        $current_url: `${safeOrigin}${route}`,
        $referrer: 'redacted',
      }),
    };
    if (client) apply(action);
    else enqueue(action);
  };

  const initialize = async (): Promise<boolean> => {
    if (!projectToken || typeof window === 'undefined') return false;
    if (client) return true;
    if (initializationPromise) return initializationPromise;
    if (initializationAttempted) return false;
    initializationAttempted = true;
    initializationPromise = options.loadClient()
      .then((loadedClient) => {
        loadedClient.init(projectToken, {
          api_host: options.host,
          autocapture: false,
          capture_pageview: false,
          capture_pageleave: false,
          disable_session_recording: true,
          disable_surveys: true,
          disable_product_tours: true,
          disable_web_experiments: true,
          disable_external_dependency_loading: true,
          advanced_disable_feature_flags: true,
          advanced_disable_toolbar_metrics: true,
          opt_in_site_apps: false,
          capture_heatmaps: false,
          capture_dead_clicks: false,
          capture_exceptions: false,
          capture_performance: false,
          person_profiles: 'identified_only',
          persistence: 'localStorage',
          mask_all_text: true,
          mask_personal_data_properties: true,
          disable_capture_url_hashes: true,
          before_send: (captureResult: AnalyticsCaptureResult | null) =>
            filterAnalyticsEvent(captureResult, projectToken),
        });

        // Preserve a same-user browser session across refreshes, but never let
        // PostHog merge an authenticated user with a different persisted user.
        let persistedIdentity: string | null = null;
        try {
          persistedIdentity = loadedClient.get_distinct_id?.() || null;
        } catch {
          // An unreadable persisted identity is treated as unsafe below.
        }
        const identityMismatch = desiredIdentity !== null && persistedIdentity !== desiredIdentity;
        let identityContextMismatch = false;
        if (desiredIdentity !== null && persistedIdentity === desiredIdentity) {
          if (!loadedClient.get_property) {
            identityContextMismatch = Object.keys(desiredIdentityProperties).length > 0;
          } else {
            identityContextMismatch = Object.entries(desiredIdentityProperties)
              .some(([key, value]) => loadedClient.get_property?.(key) !== value);
          }
        }
        if (resetOnInitialize || identityMismatch || identityContextMismatch) {
          loadedClient.reset();
        }
        resetOnInitialize = false;
        loadedClient.register(BASE_PROPERTIES);
        client = loadedClient;
        const pending = queue.splice(0, queue.length);
        pending.forEach(apply);
        return true;
      })
      .catch(() => { initializationFailed = true; queue.length = 0; measurements.clearAttribution(); return false; });
    return initializationPromise;
  };

  return {
    ...measurements,
    capturePageview,
    identify,
    initialize,
    reset,
    setProjectContext,
  };
}

const analyticsBridge = createLazyAnalyticsBridge({
  key: POSTHOG_KEY,
  host: POSTHOG_HOST,
  loadClient: async () => {
    const posthogModule = await import('posthog-js');
    return posthogModule.default as unknown as AnalyticsClient;
  },
});

export function initializeClientAnalytics(): Promise<boolean> {
  return analyticsBridge.initialize();
}

export function identifyClientAnalytics(
  distinctId: string,
  properties: AnalyticsProperties = {},
): void {
  analyticsBridge.identify(distinctId, properties);
}

export function setClientAnalyticsProject(projectId: number | null | undefined): void {
  analyticsBridge.setProjectContext(projectId);
}

export function captureClientPageview(pathname: string): void {
  analyticsBridge.capturePageview(pathname);
}

export function resetClientAnalytics(): void {
  analyticsBridge.reset();
}

export const beginProductAction = analyticsBridge.beginProductAction;
export const beginProductRequest = analyticsBridge.beginProductRequest;
export const captureNotificationOpened = analyticsBridge.captureNotificationOpened;
export { classifyProductFailure } from '../../../../shared/productAnalytics';
export type { ProductAction, ProductActionProperties, ProductActionAttempt, ProductFailureReason, ProductRequest, ProductRequestProperties, NotificationOpenProperties } from '../../../../shared/productAnalytics';
